/**
 * Text layout: wrapping, alignment and auto-shrink.
 *
 * This is where most real screenshot defects come from. A caption that fits in English can
 * be twice as long in German, Arabic reads right-to-left, and CJK has no spaces to wrap on.
 * The previous pipeline needed a hand-written collision check because clipping and overlap
 * reached production; here the layout engine is expected to prevent it, and `fit: "shrink"`
 * is the default so the *approved wording* survives rather than being truncated.
 *
 * Font size arrives as a fraction of canvas height (see the unit rules in
 * `@keymaker/schema/common.ts`) and becomes pixels here.
 */
import type { Ctx2D } from './env';
import type { Rect } from './fit';
import type { CanvasSize, TextLayer, TextStyle } from '@keymaker/schema';

export interface TextLayout {
  lines: string[];
  fontSize: number;
  /** Baseline-to-baseline distance in pixels. */
  lineHeight: number;
  /** Width of the widest line. */
  width: number;
  /** Total occupied height. */
  height: number;
  /** True when the text still does not fit at the smallest permitted size. */
  overflow: boolean;
  /** Size the template asked for, before any shrink. */
  requestedFontSize: number;
}

/** Smallest fraction of the requested size auto-shrink may fall back to. */
export const MIN_SHRINK_RATIO = 0.45;

/**
 * Paragraph direction from the first strong character, per Unicode bidi rule P2/P3.
 *
 * The renderer has no locale, and should not need one: the copy itself says which way it
 * reads. Setting `ctx.direction` lets the platform's bidi algorithm place mixed runs,
 * which is the only way a locale like `ar-SA` with embedded Latin product names comes out
 * right.
 */
export function paragraphDirection(text: string): 'ltr' | 'rtl' {
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (
      (code >= 0x0590 && code <= 0x05ff) || // Hebrew
      (code >= 0x0600 && code <= 0x06ff) || // Arabic
      (code >= 0x0700 && code <= 0x074f) || // Syriac
      (code >= 0x0780 && code <= 0x07bf) || // Thaana
      (code >= 0x07c0 && code <= 0x07ff) || // NKo
      (code >= 0x08a0 && code <= 0x08ff) || // Arabic Extended-A
      (code >= 0xfb1d && code <= 0xfdff) || // Hebrew/Arabic presentation forms
      (code >= 0xfe70 && code <= 0xfeff) // Arabic presentation forms-B
    ) {
      return 'rtl';
    }
    // A strong Latin/Cyrillic/Greek/CJK character settles it.
    if (
      (code >= 0x0041 && code <= 0x005a) ||
      (code >= 0x0061 && code <= 0x007a) ||
      (code >= 0x00c0 && code <= 0x024f) ||
      (code >= 0x0370 && code <= 0x03ff) ||
      (code >= 0x0400 && code <= 0x04ff) ||
      (code >= 0x3040 && code <= 0x30ff) ||
      (code >= 0x3400 && code <= 0x9fff) ||
      (code >= 0xac00 && code <= 0xd7af)
    ) {
      return 'ltr';
    }
  }
  return 'ltr';
}

/** Writing systems that break between characters rather than at spaces. */
function breaksAnywhere(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return (
    (code >= 0x3040 && code <= 0x30ff) || // kana
    (code >= 0x3400 && code <= 0x9fff) || // CJK ideographs
    (code >= 0xac00 && code <= 0xd7af) || // Hangul syllables
    (code >= 0xf900 && code <= 0xfaff) // CJK compatibility
  );
}

export function fontString(style: TextStyle, fontSize: number): string {
  const slant = style.italic ? 'italic ' : '';
  // A fallback stack matters: a missing family would otherwise silently fall back to a
  // default that measures differently, and text would overflow in some environments only.
  return `${slant}${style.weight} ${fontSize}px ${style.fontFamily}, Helvetica, Arial, sans-serif`;
}

/**
 * Greedy wrap honouring explicit newlines, then word boundaries, then per-character
 * boundaries for writing systems that have none.
 */
function wrapLines(ctx: Ctx2D, text: string, maxWidth: number, letterSpacing: number): string[] {
  const measure = (value: string): number => {
    if (value.length === 0) return 0;
    return ctx.measureText(value).width + letterSpacing * Math.max(value.length - 1, 0);
  };

  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    if (paragraph.length === 0) {
      lines.push('');
      continue;
    }

    // Split into wrappable units: a run of non-CJK non-space characters is one unit,
    // every CJK character is its own unit.
    const units: string[] = [];
    let buffer = '';
    for (const char of paragraph) {
      if (char === ' ' || breaksAnywhere(char)) {
        if (buffer) {
          units.push(buffer);
          buffer = '';
        }
        units.push(char);
      } else {
        buffer += char;
      }
    }
    if (buffer) units.push(buffer);

    let line = '';
    for (const unit of units) {
      const candidate = line + unit;
      if (line.length > 0 && measure(candidate.trimEnd()) > maxWidth) {
        lines.push(line.trimEnd());
        line = unit === ' ' ? '' : unit;
      } else {
        line = candidate;
      }
    }
    lines.push(line.trimEnd());
  }

  return lines;
}

interface MeasureResult {
  lines: string[];
  width: number;
  height: number;
}

function measureWith(
  ctx: Ctx2D,
  layer: TextLayer,
  fontSize: number,
  maxWidth: number,
): MeasureResult {
  const content = layer.style.uppercase ? layer.content.toUpperCase() : layer.content;
  ctx.font = fontString(layer.style, fontSize);
  const lines = layer.fit === 'none' ? content.split('\n') : wrapLines(ctx, content, maxWidth, layer.style.letterSpacing);
  const lineHeight = fontSize * layer.style.lineHeight;
  const width = lines.reduce((max, line) => {
    const w = ctx.measureText(line).width + layer.style.letterSpacing * Math.max(line.length - 1, 0);
    return Math.max(max, w);
  }, 0);
  return { lines, width, height: lines.length * lineHeight };
}

/**
 * Lay out a text layer inside its box.
 *
 * For `fit: "shrink"` the largest size that fits both the box width and height is found by
 * binary search, bounded below by `MIN_SHRINK_RATIO` of the requested size. Binary search
 * rather than a fixed ladder because the useful range differs by an order of magnitude
 * between a 122px panel title and a 43px chip label.
 */
export function layoutText(ctx: Ctx2D, layer: TextLayer, canvas: CanvasSize): TextLayout {
  const box = layerBox(layer, canvas);
  const requestedFontSize = layer.style.size * canvas.height;

  if (layer.fit === 'none') {
    const measured = measureWith(ctx, layer, requestedFontSize, box.width);
    return {
      ...measured,
      fontSize: requestedFontSize,
      lineHeight: requestedFontSize * layer.style.lineHeight,
      requestedFontSize,
      overflow: measured.width > box.width || measured.height > box.height,
    };
  }

  const fits = (size: number): MeasureResult | null => {
    const measured = measureWith(ctx, layer, size, box.width);
    const widthOk = measured.width <= box.width || size <= requestedFontSize * MIN_SHRINK_RATIO;
    const heightOk = measured.height <= box.height || size <= requestedFontSize * MIN_SHRINK_RATIO;
    return widthOk && heightOk ? measured : null;
  };

  let best = fits(requestedFontSize);
  let size = requestedFontSize;

  if (!best) {
    let low = requestedFontSize * MIN_SHRINK_RATIO;
    let high = requestedFontSize;
    let found: MeasureResult | null = null;
    let foundSize = low;
    for (let i = 0; i < 24 && high - low > 0.5; i += 1) {
      const mid = (low + high) / 2;
      const measured = fits(mid);
      if (measured) {
        found = measured;
        foundSize = mid;
        low = mid;
      } else {
        high = mid;
      }
    }
    best = found ?? measureWith(ctx, layer, low, box.width);
    size = found ? foundSize : low;
  }

  const measured = best ?? measureWith(ctx, layer, size, box.width);
  return {
    ...measured,
    fontSize: size,
    lineHeight: size * layer.style.lineHeight,
    requestedFontSize,
    overflow: measured.width > box.width + 0.5 || measured.height > box.height + 0.5,
  };
}

export function layerBox(layer: TextLayer, canvas: CanvasSize): Rect {
  return {
    x: layer.box.x * canvas.width,
    y: layer.box.y * canvas.height,
    width: layer.box.width * canvas.width,
    height: layer.box.height * canvas.height,
  };
}

/**
 * Draw laid-out text inside its box.
 *
 * Alignment is applied through `ctx.direction` rather than by computing a left offset, so
 * RTL lines align to their own reading edge and mixed-direction lines are placed by the
 * platform's bidi algorithm.
 */
export function drawText(ctx: Ctx2D, layer: TextLayer, layout: TextLayout, canvas: CanvasSize): void {
  const box = layerBox(layer, canvas);
  const direction = paragraphDirection(layer.content);

  ctx.save();
  ctx.direction = direction;
  ctx.font = fontString(layer.style, layout.fontSize);
  ctx.fillStyle = layer.style.color;
  ctx.textAlign = layer.align === 'center' ? 'center' : layer.align;
  ctx.textBaseline = 'alphabetic';
  if (layer.style.letterSpacing !== 0) {
    // Supported in modern browsers and by Skia; ignored where unavailable.
    (ctx as unknown as { letterSpacing: string }).letterSpacing = `${layer.style.letterSpacing * layout.fontSize}px`;
  }

  if (layer.rotation !== 0) {
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    ctx.translate(cx, cy);
    ctx.rotate((layer.rotation * Math.PI) / 180);
    ctx.translate(-cx, -cy);
  }

  const anchorX =
    layer.align === 'center' ? box.x + box.width / 2 : layer.align === 'right' ? box.x + box.width : box.x;

  const totalHeight = layout.lines.length * layout.lineHeight;
  const startY =
    layer.valign === 'middle'
      ? box.y + (box.height - totalHeight) / 2
      : layer.valign === 'bottom'
        ? box.y + box.height - totalHeight
        : box.y;

  layout.lines.forEach((line, index) => {
    // First baseline sits one ascender below the line box top.
    const baseline = startY + index * layout.lineHeight + layout.fontSize * 0.8;
    ctx.fillText(line, anchorX, baseline);
  });

  if (layer.style.letterSpacing !== 0) {
    (ctx as unknown as { letterSpacing: string }).letterSpacing = '0px';
  }
  ctx.restore();
}

/** Rects occupied by each line, used by the collision checks the template gate runs. */
export function lineRects(
  ctx: Ctx2D,
  layer: TextLayer,
  layout: TextLayout,
  canvas: CanvasSize,
): Rect[] {
  const box = layerBox(layer, canvas);
  ctx.save();
  ctx.font = fontString(layer.style, layout.fontSize);
  const rects = layout.lines.map((line, index) => {
    const width = ctx.measureText(line).width;
    const x =
      layer.align === 'center' ? box.x + (box.width - width) / 2 : layer.align === 'right' ? box.x + box.width - width : box.x;
    const totalHeight = layout.lines.length * layout.lineHeight;
    const startY =
      layer.valign === 'middle'
        ? box.y + (box.height - totalHeight) / 2
        : layer.valign === 'bottom'
          ? box.y + box.height - totalHeight
          : box.y;
    return {
      x,
      y: startY + index * layout.lineHeight,
      width,
      height: layout.fontSize * 1.2,
    };
  });
  ctx.restore();
  return rects;
}
